import { Box, Text } from '@chakra-ui/react';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import { countBulletLines } from '@revelio/iso-utility';

import styles from './MarkdownDescription.module.css';

const hasMarkdownContent = (children: React.ReactNode): boolean =>
  React.Children.toArray(children).some((child) => {
    if (typeof child === 'string') {
      return child.trim().length > 0;
    }
    if (typeof child === 'number') {
      return true;
    }
    if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
      const nested = child.props.children;
      return nested ? hasMarkdownContent(nested) : false;
    }
    return false;
  });

/**
 * Whether a description should render as markdown rather than plain text.
 * Already-formatted descriptions always do; otherwise we only switch to
 * markdown when there are real bullet lines to render.
 */
export const shouldRenderMarkdown = (
  description: string,
  isFormatted: boolean
): boolean => {
  if (isFormatted) {
    return true;
  }
  return countBulletLines(description) > 0;
};

export const MarkdownDescription = ({
  description,
}: {
  description: string;
}): React.ReactElement => (
  <Box className={styles.markdownContent}>
    <ReactMarkdown
      components={{
        p: ({ children }) => {
          if (!hasMarkdownContent(children)) {
            return null;
          }
          return (
            <Text
              as="p"
              className={`${styles.sectionDetails} ${styles.markdownParagraph}`}
            >
              {children}
            </Text>
          );
        },
        ul: ({ children }) => (
          <Box as="ul" className={styles.markdownList}>
            {children}
          </Box>
        ),
        li: ({ children }) => (
          <Text as="li" className={styles.markdownListItem}>
            {children}
          </Text>
        ),
      }}
    >
      {description.trimEnd()}
    </ReactMarkdown>
  </Box>
);
